import { useEffect, useMemo, useState } from 'react';
import { getCurrentWeek } from '../lib/api';
import { formatOrgDate } from '../lib/utils';
import { useClient } from '../context/ClientContext';
import WelcomeNarrative from './WelcomeNarrative';
import WeekBanner from './WeekBanner';
import Spots from './Spots';
import BlockContainer from '../blocks/BlockContainer';
import DevModePanel from './DevModePanel';

export default function ConciergeHome({
  clientId,
  chat,
  mode = 'full',
  showDevPanel = false,
  className = '',
}) {
  const { clientConfig } = useClient();
  const [weekData, setWeekData] = useState(null);
  const [isWeekLoading, setIsWeekLoading] = useState(true);
  const isSmallSurface = mode === 'compact' || mode === 'mobile';
  const showBlocks = mode !== 'mobile';
  const visibleSpots = Array.isArray(weekData?.spots) ? weekData.spots.slice(0, 3) : [];
  // The organisation's language and clock, never the visitor's browser's.
  const todayLabel = formatOrgDate(new Date(), clientConfig, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const bannerVideo = useMemo(() => {
    const videos = [
      '13522393_3840_2160_30fps.mp4',
      '2992757-uhd_3840_2160_25fps.mp4',
      '4151680-uhd_3840_2160_25fps.mp4',
      '7981540-uhd_3840_2160_24fps.mp4',
      '8303104-hd_1920_1080_24fps.mp4',
      '9150545-hd_1920_1080_24fps.mp4',
    ];
    return `/videos/${videos[Math.floor(Math.random() * videos.length)]}`;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadWeekData() {
      try {
        setIsWeekLoading(true);
        const currentWeek = await getCurrentWeek(clientId);
        if (!cancelled) {
          setWeekData(currentWeek);
        }
      } catch (error) {
        if (!cancelled) {
          setWeekData(null);
        }
        console.warn('No spotlight data available for client:', clientId, error);
      } finally {
        if (!cancelled) {
          setIsWeekLoading(false);
        }
      }
    }

    loadWeekData();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return (
    <div className={className}>
      {showDevPanel && (
        <DevModePanel
          clientId={clientId}
          sessionId={chat.sessionId}
          messageCount={chat.messages.length}
        />
      )}

      {!isSmallSurface && (
        <div className="pt-12 pb-8">
          <WelcomeNarrative
            title={
              clientConfig?.heading || (
                <>
                  What do you need help
                  <br className="sm:hidden" /> with today?
                </>
              )
            }
            subtitle={
              clientConfig?.subheading ||
              clientConfig?.askPlaceholder ||
              'Ask about your membership, continuing education, events, or certification requirements.'
            }
          />
        </div>
      )}

      <div className={isSmallSurface ? 'space-y-4 pb-4 pt-3' : 'space-y-6 pb-8'}>
        {isWeekLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex gap-1">
              <div
                className="h-2 w-2 rounded-full bg-stone-300 animate-bounce-custom"
                style={{ animationDelay: '0ms' }}
              />
              <div
                className="h-2 w-2 rounded-full bg-stone-300 animate-bounce-custom"
                style={{ animationDelay: '150ms' }}
              />
              <div
                className="h-2 w-2 rounded-full bg-stone-300 animate-bounce-custom"
                style={{ animationDelay: '300ms' }}
              />
            </div>
          </div>
        )}

        {!isWeekLoading &&
          weekData &&
          (isSmallSurface ? (
            <div className="-mx-4 -mt-3 sm:-mx-5 px-4 pt-5 sm:px-5">
              <WeekBanner week={weekData} mode={mode} />
              <Spots spots={visibleSpots} mode={mode} />
            </div>
          ) : (
            <>
              <WeekBanner week={weekData} mode={mode} />
              <Spots spots={visibleSpots} mode={mode} />
            </>
          ))}

        {!isWeekLoading && !weekData && (
          <div
            className={`overflow-hidden border border-slate-200/80 ${isSmallSurface ? 'h-24 rounded-[14px]' : 'h-32 rounded-2xl sm:h-40'}`}
          >
            <video autoPlay loop muted playsInline className="h-full w-full scale-150 object-cover">
              <source src={bannerVideo} type="video/mp4" />
            </video>
          </div>
        )}

        {mode === 'full' && (
          <div className="flex flex-col items-center gap-3 border-t border-slate-100 py-4">
            <div className="flex items-center gap-3 text-sm text-slate-400">
              <div className="h-px w-10 bg-slate-200 sm:w-12" />
              <span>{todayLabel}</span>
              <div className="h-px w-10 bg-slate-200 sm:w-12" />
            </div>
            <div className="max-w-full">{chat.authBar}</div>
            {chat.authInline}
          </div>
        )}

        {showBlocks && <BlockContainer clientId={clientId} />}
      </div>
    </div>
  );
}
